import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { CreateWarrantyDto, DurationOption } from '../types/warranty.types';
import type { WarrantyPreset } from '../types/warranty-preset.types';
import { mapWarrantyFormToDto } from '../services/warrantyFormMapper';
import { DEFAULT_WARRANTY_DIAGNOSTIC } from '../constants/warranty-defaults';
import { useAuth } from '@/hooks/auth/useAuth';
import { useSociety } from '@/features/societies/hooks/useSocieties';

/**
 * État du formulaire wizard de création de garantie
 */
export interface WarrantyFormState {
  // Step 1: Société et Type
  societyId?: string;
  warrantyType?: 0 | 1 | 2; // 0=WARRANTY, 1=SERVICE, 2=EXTENSION

  // Step 2: Informations générales
  name: string;
  template?: string;
  vehicleType?: number;
  duration?: DurationOption;

  // Step 3: Pricing
  sellingPrice?: number;
  /**
   * Prix de la prestation Affinicar. Le wizard ne le renseigne PAS : il n'offre
   * aucun champ pour le saisir et l'API le résout depuis la grille tarifaire.
   * Conservé pour `PricingFields`, qui expose un champ dédié.
   */
  servicePrice?: number;
  /**
   * Formule d'assistance retenue (`null` = aucune). Le supplément tarifaire est
   * résolu côté serveur à partir de la société et de la durée — le formulaire ne
   * transporte que le choix.
   */
  assistanceFormulaId?: number | null;
  financialCap?: number;
  kmLimit?: number;
  headTransferPrice?: number;
  memberTransferPrice?: number;
  provision?: number;
  commission?: number;
  commissionType?: 1 | 2; // 1=montant fixe (€), 2=pourcentage du HT (%)

  // Step 4: Pièces couvertes
  selectedPartIds: number[];

  // Step 5: Options
  isMensualisable: boolean;
  isCashPayment: boolean;
  isBroadcast: boolean;
  isActive: boolean;
  diagnostic?: string;

  // Preset warranty appliqué
  appliedWarrantyPresetId?: number | null;
}

const INITIAL_STATE: WarrantyFormState = {
  name: '',
  selectedPartIds: [],
  isMensualisable: false,
  isCashPayment: false,
  isBroadcast: true,
  isActive: true,
  appliedWarrantyPresetId: null,
  diagnostic: DEFAULT_WARRANTY_DIAGNOSTIC,
};

const SESSION_STORAGE_KEY = 'warranty-wizard-session';
const SESSION_TTL_MS = 4 * 60 * 60 * 1000; // 4 heures

interface SessionSnapshot {
  formData: WarrantyFormState;
  currentStep: number;
  touchedSteps: number[];
  savedAt: number;
}

function getSessionSnapshot(): SessionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const snapshot: SessionSnapshot = JSON.parse(raw);
    if (Date.now() - snapshot.savedAt > SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Hook de gestion du wizard de création/édition de garantie
 */
export const useWarrantyFormWizard = (initialSocietyId?: string) => {
  const { user } = useAuth();

  // Déterminer si l'utilisateur est externe
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);

  const initialFormSocietyId = initialSocietyId || (isExternalUser ? user?.societyId : undefined);

  // Un brouillon d'une autre société (ex. commencé pour l'agence, repris depuis
  // la tête de groupe) ne doit pas être restauré : il créerait la garantie
  // dans la mauvaise stratégie.
  const [sessionSnapshot] = useState(() => {
    const snapshot = getSessionSnapshot();
    if (snapshot && initialFormSocietyId && snapshot.formData.societyId !== initialFormSocietyId) return null;
    return snapshot;
  });
  const isInitialized = useRef(false);

  // Initialiser l'état avec societyId pour les externes, ou restaurer depuis sessionStorage
  const [formData, setFormData] = useState<WarrantyFormState>(() => {
    if (sessionSnapshot) return { ...INITIAL_STATE, ...sessionSnapshot.formData };
    return {
      ...INITIAL_STATE,
      societyId: initialFormSocietyId,
    };
  });

  const [currentStep, setCurrentStep] = useState(() => sessionSnapshot?.currentStep ?? 1);
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(() =>
    sessionSnapshot ? new Set(sessionSnapshot.touchedSteps) : new Set([1])
  );

  // Persister dans sessionStorage à chaque changement (après l'init)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    try {
      const snapshot: SessionSnapshot = {
        formData,
        currentStep,
        touchedSteps: [...touchedSteps],
        savedAt: Date.now(),
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(snapshot));
    } catch { /* sessionStorage plein ou indisponible */ }
  }, [formData, currentStep, touchedSteps]);

  // Société de la garantie : sert à la mensualisation, provision et commission
  const { data: effectiveSociety } = useSociety(formData.societyId);

  // Déterminer si la mensualisation est disponible
  const canMensualize = useMemo(() => {
    return effectiveSociety?.monthlyPayment === 2;
  }, [effectiveSociety?.monthlyPayment]);

  // Nombre total de steps (fixe : 4 steps)
  const totalSteps = 4;

  /**
   * Met à jour les données du formulaire
   */
  const updateFormData = useCallback((updates: Partial<WarrantyFormState>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Met à jour un champ spécifique
   */
  const updateField = useCallback(
    <K extends keyof WarrantyFormState>(field: K, value: WarrantyFormState[K]) => {
      updateFormData({ [field]: value });
    },
    [updateFormData],
  );

  /**
   * Gestion des pièces
   */
  const addPart = useCallback((partId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedPartIds: [...prev.selectedPartIds, partId],
    }));
  }, []);

  const removePart = useCallback((partId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedPartIds: prev.selectedPartIds.filter((id) => id !== partId),
    }));
  }, []);

  const togglePart = useCallback((partId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedPartIds: prev.selectedPartIds.includes(partId)
        ? prev.selectedPartIds.filter((id) => id !== partId)
        : [...prev.selectedPartIds, partId],
    }));
  }, []);

  const clearParts = useCallback(() => {
    setFormData((prev) => ({ ...prev, selectedPartIds: [] }));
  }, []);

  const setParts = useCallback((partIds: number[]) => {
    setFormData((prev) => ({ ...prev, selectedPartIds: partIds }));
  }, []);

  /**
   * Valide un step spécifique
   */
  const validateStep = useCallback(
    (step: number): { isValid: boolean; errors: string[] } => {
      const errors: string[] = [];

      switch (step) {
        case 1: // Informations (Société + Type + Nom + Véhicule + Diagnostic)
          if (!formData.societyId) errors.push('Société obligatoire');
          if (formData.warrantyType === undefined) errors.push('Type de garantie obligatoire');
          if (!formData.name || formData.name.length < 3)
            errors.push('Nom obligatoire (min 3 caractères)');
          if (formData.vehicleType === undefined) errors.push('Type de véhicule obligatoire');
          break;

        case 2: // Configuration (Durée + Plafond + Limite km)
          if (formData.warrantyType !== 2) {
            if (!formData.duration) errors.push('Durée obligatoire');
            if (!formData.kmLimit || formData.kmLimit <= 0)
              errors.push('Limite kilométrique obligatoire');
          }
          if (!formData.financialCap || formData.financialCap <= 0)
            errors.push('Plafond financier obligatoire');
          break;

        case 3: // Pièces
          if (formData.selectedPartIds.length === 0) {
            errors.push('Au moins une pièce doit être sélectionnée');
          }
          break;

        case 4: // Tarification (Options de paiement + Prix + Transfer + Commission + Provision)
          // Options de paiement obligatoires
          if (formData.isCashPayment === undefined || formData.isMensualisable === undefined) {
            errors.push('Options de paiement obligatoires');
          }
          // Prix de vente
          if (!formData.sellingPrice || formData.sellingPrice <= 0)
            errors.push('Prix de vente obligatoire');
          // Validation conditionnelle de provision
          if (effectiveSociety?.defineProvision === true) {
            if (!formData.provision || formData.provision <= 0) {
              errors.push('Provision obligatoire');
            }
          }
          // Validation conditionnelle de commission
          if (effectiveSociety?.commission === true) {
            if (!formData.commission || formData.commission <= 0) {
              errors.push('Commission obligatoire');
            }
          }
          break;
      }

      return { isValid: errors.length === 0, errors };
    },
    [formData, effectiveSociety],
  );

  /**
   * Navigation
   */
  const goToNextStep = useCallback(() => {
    const validation = validateStep(currentStep);
    if (!validation.isValid) {
      console.warn('Validation échouée:', validation.errors);
      return false;
    }

    const nextStep = currentStep + 1;
    if (nextStep <= totalSteps) {
      setCurrentStep(nextStep);
      setTouchedSteps((prev) => new Set(prev).add(nextStep));
      return true;
    }
    return false;
  }, [currentStep, totalSteps, validateStep]);

  const goToPreviousStep = useCallback(() => {
    const prevStep = currentStep - 1;
    if (prevStep >= 1) {
      setCurrentStep(prevStep);
      return true;
    }
    return false;
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      setTouchedSteps((prev) => new Set(prev).add(step));
      return true;
    }
    return false;
  }, [totalSteps]);

  /**
   * Vérifie si un step a été touché
   */
  const isStepTouched = useCallback(
    (step: number): boolean => {
      return touchedSteps.has(step);
    },
    [touchedSteps],
  );

  /**
   * Vérifie si un step est valide
   */
  const isStepValid = useCallback(
    (step: number): boolean => {
      return validateStep(step).isValid;
    },
    [validateStep],
  );


  /**
   * Convertit le formData en DTO pour l'API
   */
  const toCreateDto = useCallback((): CreateWarrantyDto => {
    return mapWarrantyFormToDto(formData, canMensualize);
  }, [formData, canMensualize]);

  /**
   * Réinitialise le formulaire et supprime la session sauvegardée
   */
  const reset = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setFormData({
      ...INITIAL_STATE,
      societyId: initialSocietyId || (isExternalUser ? user?.societyId : undefined),
    });
    setCurrentStep(1);
    setTouchedSteps(new Set([1]));
  }, [initialSocietyId, isExternalUser, user?.societyId]);

  /**
   * Applique un preset de garantie au formulaire.
   *
   * Ne recopie QUE des caractéristiques de produit (durée, plafond, pièces,
   * prix...). Les flags de cycle de vie de la garantie — `isBroadcast`,
   * `isActive` — restent ceux du formulaire (`INITIAL_STATE`) : un preset est
   * un gabarit de configuration, pas une décision de diffusion. Historiquement
   * `preset.isBroadcast` (« Diffusée aux prospects ») était recopié dans
   * `IS_BROADCAST`, qui pilote en réalité la visibilité de la garantie partout
   * dans l'outil — un preset décoché créait des garanties introuvables.
   *
   * Le nom repris est le `label` (« Garantie Essentielle »), pas le `name`
   * technique (« essential »), et il est mis en majuscules comme le fait la
   * saisie manuelle du Step 1.
   *
   * `template` n'est volontairement PAS repris : `mapWarrantyFormToDto` ne
   * transmet pas ce champ à l'API, le poser ici ne ferait que laisser croire
   * qu'il est propagé. Le rendre effectif demanderait d'abord d'aligner les
   * longueurs — `pro_v2_warranty_preset.template` est un TEXT non validé, alors
   * que `CreateWarrantyDto.template` est plafonné à 100 caractères.
   */
  const applyPreset = useCallback((preset: WarrantyPreset) => {
    updateFormData({
      name: (preset.label || preset.name || '').toUpperCase(),
      warrantyType: 0, // Garantie : un preset ne décrit pas de service ni d'extension
      vehicleType: preset.vehicleType,
      duration: preset.duration,
      financialCap: preset.financialCap,
      kmLimit: preset.kmLimit,
      sellingPrice: preset.sellingPrice,
      diagnostic: preset.diagnostic ?? DEFAULT_WARRANTY_DIAGNOSTIC,
      selectedPartIds: preset.partIds || [],
      appliedWarrantyPresetId: preset.id,
    });
  }, [updateFormData]);

  return {
    // État
    currentStep,
    totalSteps,
    formData,
    touchedSteps,
    canMensualize,

    // Actions formulaire
    updateFormData,
    updateField,

    // Actions pièces
    addPart,
    removePart,
    togglePart,
    clearParts,
    setParts,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,

    // Validation
    validateStep,
    isStepTouched,
    isStepValid,

    // Conversion
    toCreateDto,

    // Presets
    applyPreset,

    // Reset
    reset,
  };
};
